import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingOutlined } from '@ant-design/icons';
import { DeprecatedFAIcon } from '@components-library/Icons/DeprecatedFAIcon';
import { DEPRECATED_FA_GLOBAL_SPRITE } from '@components-library/Icons/interfaces';
import { keyMap } from '@locales/lokalise/keyMap';
import { Input, InputRef, Spin } from 'antd';
import debounce from 'lodash/debounce';
import styles from './styles.module.scss';

interface ISelectSearchInputProps {
  searchRequest?: string;
  setSearchRequest: (value?: string) => void;
  loading: boolean;
  getOptionsData: (value?: string) => Promise<void>;
  setFocusSelector: () => void;
}

export const SelectSearchInput = ({
  searchRequest,
  setSearchRequest,
  loading,
  getOptionsData,
  setFocusSelector,
}: ISelectSearchInputProps) => {
  const { i18n } = useTranslation();
  const inputRef = React.useRef<InputRef>(null);

  const debouncedGetOptionsData = useMemo(
    () =>
      debounce(async (value?: string) => {
        await getOptionsData(value);
      }, 500),
    [getOptionsData],
  );

  const clearFilteringData = async () => {
    setSearchRequest(undefined);
    await getOptionsData(undefined);
  };

  const handlerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (inputRef.current) {
        setFocusSelector();
        inputRef.current.blur();
      }
    }
  };

  const onChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    setSearchRequest(value);

    await debouncedGetOptionsData(value);
  };

  const handleSubmit = async (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
      await getOptionsData(searchRequest);
    }
  };

  const renderInputIcon = () => {
    if (loading) {
      return <Spin indicator={<LoadingOutlined className={styles.searchBlock__loading} spin />} />;
    }

    return (
      <>
        {searchRequest && (
          <DeprecatedFAIcon
            icon={DEPRECATED_FA_GLOBAL_SPRITE.backspace}
            className={styles.searchBlock__clearIcon}
            onClick={clearFilteringData}
          />
        )}

        <DeprecatedFAIcon
          icon={DEPRECATED_FA_GLOBAL_SPRITE.search}
          className={styles.searchBlock__icon}
          onClick={async () => {
            await getOptionsData(searchRequest);
          }}
        />
      </>
    );
  };

  return (
    <div className={styles.searchBlock__inputWrapper}>
      <Input
        className={styles.searchBlock__input}
        placeholder={i18n.t(keyMap.tradingAccountsSearchPlaceholder)}
        ref={inputRef}
        autoFocus
        value={searchRequest}
        onPressEnter={handleSubmit}
        onChange={onChange}
        suffix={renderInputIcon()}
        onKeyDown={handlerKeyDown}
      />
    </div>
  );
};
